import type {Component, Editor} from 'grapesjs';
import {EMAIL_MAKER_COMPONENT_TYPES} from '@EmailMakerReact/email-maker-react.constants';
import {AutoBrandingControl} from '../../controls/auto-branding-control/auto-branding-control';
import {makeColorControl} from '../../controls/color-control/color-control';
import {contentBgColorDescriptor, emailBgColorDescriptor} from '../../controls/color-control/color-control-descriptors';
import {makeSliderControl} from '../../controls/slider-control/slider-control';
import {borderRadiusDescriptor, limitedWidthDescriptor, paddingDescriptor} from '../../controls/slider-control/slider-control-descriptors';
import {makeStyleHeader} from '../../controls/style-header/style-header';
import {makeMobileSheetGroup} from '../../controls/mobile-sheet-group/mobile-sheet-group';
import {makeAiRedesignControl} from '../../controls/ai-content-control/ai-content-control';
import {TemplateNameInputControl} from '../../controls/template-name-input-control/template-name-input-control';
import {registerEmailMakerSector} from '../../right-panel-registry';
import {TileComplexity} from '@EmailMakerReact/lib/email-maker-tile-meta-library.types';
import {borderStyleControl, conditionalBorderColorControl, conditionalBorderWidthControl} from '../shared/border-controls';
import {isAiTextEnabled} from '../shared/sector-predicates';
import type {EmailMakerControl, EmailMakerSectorProperty} from '../../right-panel-registry.types';

const emailHeader: EmailMakerControl = makeStyleHeader('pmwjs_email');
const contentHeader: EmailMakerControl = makeStyleHeader('pmwjs_content');
const aiRedesignControl: EmailMakerControl = makeAiRedesignControl();
const emailBgColorControl = makeColorControl(emailBgColorDescriptor);
const contentBgColorControl = makeColorControl(contentBgColorDescriptor);
const emailPaddingControl = makeSliderControl(paddingDescriptor);
const contentWidthControl = makeSliderControl(limitedWidthDescriptor);
const borderRadiusControl = makeSliderControl(borderRadiusDescriptor);
const paddingControl = makeSliderControl(paddingDescriptor);

/** Resolves the wrapper's PMW_CONTENT child for controls that style the content container. */
function resolveContentTarget(_selected: Component, editor: Editor): Component | undefined {
  const wrapper = editor.getWrapper();
  if (!wrapper) {
    return undefined;
  }
  const contentComponents = wrapper.findType(EMAIL_MAKER_COMPONENT_TYPES.container.PMW_CONTENT);
  return contentComponents[0];
}

const emailBgProperty: EmailMakerSectorProperty = {
  control: emailBgColorControl,
  tileMeta: {icon: 'icon-fill', labelKey: 'pmwjs_email_background', complexity: TileComplexity.SIMPLE},
};
const emailSpacingProperty: EmailMakerSectorProperty = {
  control: emailPaddingControl,
  tileMeta: {icon: 'icon-edge-spacing', labelKey: 'pmwjs_spacing', complexity: TileComplexity.COMPLEX},
};
const contentBgProperty: EmailMakerSectorProperty = {
  control: contentBgColorControl,
  resolveTarget: resolveContentTarget,
  tileMeta: {icon: 'icon-fill', labelKey: 'pmwjs_content_background', complexity: TileComplexity.SIMPLE},
};
const contentWidthProperty: EmailMakerSectorProperty = {
  control: contentWidthControl,
  resolveTarget: resolveContentTarget,
  tileMeta: {icon: 'icon-stretch-horizontal', labelKey: 'pmwjs_width', complexity: TileComplexity.SIMPLE},
};
const borderRadiusProperty: EmailMakerSectorProperty = {
  control: borderRadiusControl,
  resolveTarget: resolveContentTarget,
  tileMeta: {icon: 'icon-corner', labelKey: 'pmwjs_roundness', complexity: TileComplexity.COMPLEX},
};

const wrapperBorderControl = makeMobileSheetGroup([borderStyleControl, conditionalBorderColorControl, conditionalBorderWidthControl]);
const borderProperty: EmailMakerSectorProperty = {
  control: wrapperBorderControl,
  resolveTarget: resolveContentTarget,
  tileMeta: {icon: 'icon-stroke', labelKey: 'pmwjs_border', complexity: TileComplexity.SIMPLE},
};

const WRAPPER_PROPERTIES: readonly EmailMakerSectorProperty[] = [
  {control: aiRedesignControl, isVisible: isAiTextEnabled},
  {control: AutoBrandingControl},
  {control: emailHeader},
  emailBgProperty,
  emailSpacingProperty,
  {control: contentHeader},
  contentBgProperty,
  contentWidthProperty,
  {control: borderStyleControl, resolveTarget: resolveContentTarget, tileMeta: {icon: 'icon-stroke', labelKey: 'pmwjs_border', complexity: TileComplexity.SIMPLE}},
  {control: conditionalBorderColorControl, resolveTarget: resolveContentTarget, tileMeta: {icon: 'icon-colorpicker', labelKey: 'pmwjs_border_color', complexity: TileComplexity.SIMPLE}},
  {control: conditionalBorderWidthControl, resolveTarget: resolveContentTarget, tileMeta: {icon: 'icon-stroke', labelKey: 'pmwjs_thickness', complexity: TileComplexity.SIMPLE}},
  borderRadiusProperty,
  {control: paddingControl, tileMeta: {icon: 'icon-edge-spacing', labelKey: 'pmwjs_spacing', complexity: TileComplexity.COMPLEX}},
];

const WRAPPER_MOBILE_PROPERTIES: readonly EmailMakerSectorProperty[] = [
  contentBgProperty,
  emailBgProperty,
  emailSpacingProperty,
  contentWidthProperty,
  borderProperty,
  borderRadiusProperty,
];

registerEmailMakerSector(EMAIL_MAKER_COMPONENT_TYPES.container.PMW_WRAPPER, {
  title: window.i18next.t('pmwjs_email'),
  hideTitle: true,
  headerControl: TemplateNameInputControl,
  properties: WRAPPER_PROPERTIES,
  mobileProperties: WRAPPER_MOBILE_PROPERTIES,
});
